
// client/src/components/AuctionRaffleSetup.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaClock, FaPlay, FaCopy, FaBolt, FaSave, FaArrowLeft, FaHome } from "react-icons/fa";
import AppFooter from "../AppFooter";

/* ---------- helpers ---------- */
const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith("/") ? "" : "/"}${p}`;

const baseUrl =
  (typeof process !== "undefined" && process.env?.REACT_APP_BASE_URL) ||
  (typeof window !== "undefined" && window.__BASE_URL__) ||
  "";

const nowMySQL = () => {
  const d = new Date();
  const two = (n) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(
    d.getHours()
  )}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
};

// ✅ NEW: Helper to add hours to a date and return MySQL datetime
const addHoursToNow = (hours) => {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  const two = (n) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(
    d.getHours()
  )}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
};

// const fromInputToMySQLDateTime = (local) => {
//   if (!local) return nowMySQL();
//   const [d, t] = local.split("T");
//   return `${d} ${t || "00:00"}:00`;
// };

// const fromMySQLToInput = (dt) => {
//   if (!dt) return "";
//   const [d, t] = String(dt).split(" ");
//   const hm = (t || "00:00:00").slice(0, 5);
//   return `${d}T${hm}`;
// };

const fromInputToMySQLDateTime = (local) => {
  if (!local) return nowMySQL();
  
  // Parse datetime-local value as local time
  const localDate = new Date(local);
  
  // Format as MySQL datetime in UTC
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return `${localDate.getUTCFullYear()}-${pad(localDate.getUTCMonth() + 1)}-${pad(localDate.getUTCDate())} ${pad(localDate.getUTCHours())}:${pad(localDate.getUTCMinutes())}:${pad(localDate.getUTCSeconds())}`;
};

const fromMySQLToInput = (dt) => {
  if (!dt) return "";
  
  // Parse MySQL datetime as UTC
  const utcDate = new Date(dt.replace(' ', 'T') + 'Z');
  
  // Format as datetime-local (browser converts to local timezone automatically)
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return `${utcDate.getFullYear()}-${pad(utcDate.getMonth() + 1)}-${pad(utcDate.getDate())}T${pad(utcDate.getHours())}:${pad(utcDate.getMinutes())}`;
};

/* ---------- attribute helpers ---------- */
function coerceObj(maybe, context = 'unknown') {
  if (!maybe) return {};
  if (typeof maybe === "string") {
    try { 
      return JSON.parse(maybe); 
    } catch (err) {
      console.error(`JSON parse failed in ${context}:`, err, maybe);
      return {};
    }
  }
  if (Array.isArray(maybe)) {
    const out = {};
    for (const row of maybe) {
      const k = row?.name ?? row?.attribute ?? row?.attribute_name ?? row?.key ?? row?.attr ?? row?.attributename;
      const v = row?.value ?? row?.attribute_value ?? row?.val ?? row?.data ?? row?.text ?? row?.attributevalue;
      if (!k) continue;
      out[String(k).trim()] = v;
    }
    return out;
  }
  if (typeof maybe === "object") return maybe;
  return {};
}

// Parse ALL attributes from item - handles both array and object formats
function parseAllAttributes(attributes) {
  // If it's an array of attribute rows from database
  if (Array.isArray(attributes)) {
    const grouped = {};
    
    for (const row of attributes) {
      const attrName = row?.attributename ?? row?.attribute_name ?? row?.name ?? row?.attribute;
      const attrValue = row?.attributevalue ?? row?.attribute_value ?? row?.value ?? row?.val;
      
      if (!attrName || !attrValue) continue;
      
      const name = String(attrName).trim();
      const value = String(attrValue).trim();
      
      if (!grouped[name]) {
        grouped[name] = new Set();
      }
      grouped[name].add(value);
    }
    
    // Convert Sets to sorted arrays
    const result = {};
    for (const [name, valueSet] of Object.entries(grouped)) {
      result[name] = Array.from(valueSet).sort();
    }
    
    return result;
  }
  
  // Otherwise try to parse as object
  const obj = coerceObj(attributes, 'parseAllAttributes');
  const result = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (!value) continue;
    
    // Handle array values
    if (Array.isArray(value)) {
      result[key] = value.map(v => String(v).trim()).filter(Boolean).sort();
    }
    // Handle comma/pipe separated values
    else if (typeof value === 'string') {
      const split = value.split(/[,|]/).map(x => x.trim()).filter(Boolean);
      result[key] = split.length > 1 ? split.sort() : [value.trim()];
    }
    else {
      result[key] = [String(value).trim()];
    }
  }
  
  return result;
}

/* ---------- component ---------- */
export default function AuctionRaffleSetup() {
  const { tenant: tenantIdParam, eventId: eventIdParam, itemId: itemIdParam } = useParams();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const qs = new URLSearchParams(search);

  const tenantId = Number(tenantIdParam);
  const eventId = Number(eventIdParam);
  const itemId = Number(itemIdParam);

  // Get existing data from navigation state if present
  const existingData = state?.existingData || null;
  const existingType = state?.type || null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  // item/media
  const [item, setItem] = useState(null);
  const [rawAttributes, setRawAttributes] = useState(null);
  const [media, setMedia] = useState([]);

  // config UI - Initialize actionType from state or query params
  const [actionType] = useState(
    existingType || qs.get("type") || "raffle"
  );
  const [qty, setQty] = useState(Number(qs.get("qty") || 1));
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [mode, setMode] = useState(qs.get("mode") || "schedule");
  const [startAt, setStartAt] = useState(qs.get("start_at") || "");
  const [endAt, setEndAt] = useState(qs.get("end_at") || "");
  const [startingBid, setStartingBid] = useState(qs.get("starting_bid") || "");

  const goHome = () => navigate(`/${tenantId}/eventList`);

  // load item + media
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    
    (async () => {
      try {
        setLoading(true);
        const [itemRes, imagesRes, attrsRes] = await Promise.all([
          axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}`, { 
            withCredentials: true,
            signal: controller.signal 
          }).catch(()=>({ data:{} })),
          axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/images`, { 
            withCredentials: true,
            signal: controller.signal 
          }).catch(()=>({ data:{ images:[] } })),
          axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/attributes`, { 
            withCredentials: true,
            signal: controller.signal 
          }).catch(()=>({ data:{} })),
        ]);
        if (!alive) return;

        const it = itemRes?.data?.item || itemRes?.data || {};
        setItem(it);

        const attrsFromEndpoint = attrsRes?.data?.attributes ?? attrsRes?.data?.rows ?? attrsRes?.data?.data ?? attrsRes?.data ?? null;
        const attrsFromItem = it?.attributes ?? it?.itemAttributes ?? it?.attribute_list ?? it?.attrs ?? null;
        const attributesToUse = (Array.isArray(attrsFromEndpoint) && attrsFromEndpoint.length > 0) 
          ? attrsFromEndpoint 
          : attrsFromItem;
        
        console.log('Raw attributes from API:', attributesToUse);
        setRawAttributes(attributesToUse);

        const allImages = imagesRes?.data?.images || [];
        const imgs = allImages
          .filter(i => /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(i.path || ''))
          .map((i) => ({ url: toAbs(baseUrl, i.path), isVideo: false }));

        const vids = allImages
          .filter(v => /\.(mp4|webm|mov|m4v|ogg)$/i.test(v.path || ''))
          .map((v) => ({ url: toAbs(baseUrl, v.path), isVideo: true }));

        setMedia([...imgs, ...vids]);
        console.log('✅ Media loaded:', imgs.length, 'images,', vids.length, 'videos');
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error("Load error", e);
        setStatus("Error loading item/media");
      } finally {
        setLoading(false);
      }
    })();
    
    return () => { 
      alive = false;
      controller.abort();
    };
  }, [tenantId, eventId, itemId]);

  // Parse all attributes
  const allAttributes = useMemo(() => {
    if (rawAttributes) {
      const parsed = parseAllAttributes(rawAttributes);
      console.log('Parsed attributes:', parsed);
      return parsed;
    }
    
    if (item) {
      const itemAttrs = item?.attributes ?? item?.itemAttributes ?? item?.attribute_list ?? item?.attrs;
      if (itemAttrs) {
        const parsed = parseAllAttributes(itemAttrs);
        console.log('Parsed attributes from item:', parsed);
        return parsed;
      }
    }
    
    return {};
  }, [item, rawAttributes]);

  // Public link (EventAction)
  const publicLink = useMemo(() => {
    const u = new URL(window.location.origin);
    u.pathname = `/${tenantId}/${eventId}/${itemId}/eventAction`;
    const params = new URLSearchParams();
    if (actionType) params.set("type", actionType);
    
    for (const [key, value] of Object.entries(selectedAttributes)) {
      if (value) params.set(key, value);
    }
    
    u.search = params.toString();
    return u.toString();
  }, [tenantId, eventId, itemId, actionType, selectedAttributes]);

  const copyLink = async () => {
    try { 
      await navigator.clipboard.writeText(publicLink); 
      setStatus("Link copied to clipboard"); 
      setTimeout(()=>setStatus(""), 2000); 
    }
    catch { setStatus("Failed to copy link"); }
  };

  // Load existing data from navigation state
  useEffect(() => {
    if (!existingData) return;

    console.log("Loading existing data:", existingData);

    if (existingData.winners_to_pick !== undefined) {
      setQty(Number(existingData.winners_to_pick));
    }
    if (existingData.winners_per_combo !== undefined) {
      setQty(Number(existingData.winners_per_combo));
    }
    if (existingData.start_at) {
      setStartAt(fromMySQLToInput(existingData.start_at));
      setMode('schedule');
    }
    if (existingData.end_at) {
      setEndAt(fromMySQLToInput(existingData.end_at));
    }
    if (existingData.starting_bid != null) {
      setStartingBid(String(existingData.starting_bid));
    }
    
    if (!existingData.start_at) {
      setMode('now');
    }
  }, [existingData]);

  // Prefill from saved raffle/auction config
  useEffect(() => {
    if (existingData) return;

    let alive = true;
    const controller = new AbortController();
    
    (async () => {
      try {
        const r = await axios.get(
          `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}`,
          { withCredentials: true, signal: controller.signal }
        ).catch(()=>null);
        
        if (alive && r?.data?.raffle) {
          const x = r.data.raffle;
          if (x.winners_per_combo) setQty(Number(x.winners_per_combo));
          if (x.start_at) setStartAt(fromMySQLToInput(x.start_at));
          if (x.end_at)   setEndAt(fromMySQLToInput(x.end_at));
        }

        const a = await axios.get(
          `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}`,
          { withCredentials: true, signal: controller.signal }
        ).catch(()=>null);
        
        if (alive && a?.data?.auction) {
          const y = a.data.auction;
          if (y.winners_to_pick) setQty(Number(y.winners_to_pick));
          if (y.start_at) setStartAt(fromMySQLToInput(y.start_at));
          if (y.end_at)   setEndAt(fromMySQLToInput(y.end_at));
          if (y.starting_bid != null) setStartingBid(String(y.starting_bid));
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('Prefill error:', e);
      }
    })();
    
    return () => { 
      alive = false;
      controller.abort();
    };
  }, [tenantId, eventId, itemId, existingData, baseUrl]);

  // ✅ FIXED: Submit handler with proper end_at logic for immediate mode
  const submit = async () => {
    setStatus("");
    setSaving(true);
    
    try {
      let start_at, end_at, statusVal;
      
      if (mode === "schedule") {
        // Validate scheduled mode inputs
        if (!startAt || !endAt) {
          setStatus("Please select start and end dates for scheduled mode");
          setSaving(false);
          return;
        }
        
        start_at = fromInputToMySQLDateTime(startAt);
        end_at = fromInputToMySQLDateTime(endAt);
        
        // Validate end is after start
        if (new Date(end_at) <= new Date(start_at)) {
          setStatus("End date must be after start date");
          setSaving(false);
          return;
        }
        
        statusVal = "NEW";
      } else {
        // ✅ FIX: Immediate mode - set proper end date 24 hours in future
        start_at = nowMySQL();
        end_at = addHoursToNow(24); // Default 24 hour duration for "now" mode
        statusVal = "ACTIVE";
      }
      
      const winnersCount = Number(qty) > 0 ? Number(qty) : 1;

      if (actionType === "raffle") {
        await axios.post(
          `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/config`,
          {
            tenantid: tenantId,
            eventid: eventId,
            name: "Raffle",
            winnersPerCombo: winnersCount,
            start_at, 
            end_at,
            status: statusVal,
            attributes: selectedAttributes
          },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/config`,
          {
            tenantid: tenantId,
            name: "Auction",
            winners_to_pick: winnersCount,
            start_at, 
            end_at,
            status: statusVal,
            starting_bid: startingBid !== "" ? Number(startingBid) : null,
          },
          { withCredentials: true }
        );
      }

      // Best-effort legacy insert (non-blocking)
      try {
        const toTime = (dt) => (String(dt).split(" ")[1] || "00:00:00");
        await axios.post(
          `${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/action`,
          {
            actionType, 
            url: publicLink,
            startdate: start_at,
            enddate: end_at,
            starttime: toTime(start_at)
          },
          { withCredentials: true }
        );
      } catch (e) {
        console.warn("Legacy action insert failed (ignored):", e?.response?.data || e?.message);
      }

      setStatus(`${actionType === "raffle" ? "Raffle" : "Auction"} saved. ${statusVal === "ACTIVE" ? "It is now live." : "It is scheduled."}`);
    } catch (e) {
      console.error('Submit error:', e);
      setStatus(`Error: ${e.response?.data?.message || e.message || `Failed to save ${actionType}`}`);
    } finally {
      setSaving(false);
    }
  };

  // Manual run
  const runRaffleNow = async () => {
    setSaving(true);
    setStatus("Running raffle…");
    try {
      const body = { tenantid: tenantId };
      const take = Number(qty);
      if (Number.isInteger(take) && take > 0) body.qty = take;
      if (Object.keys(selectedAttributes).length > 0) {
        body.filters = selectedAttributes;
      }
      const { data } = await axios.post(
        `${baseUrl}/api/events/${eventId}/items/${itemId}/raffle-draw`,
        body,
        { withCredentials: true }
      );
      const count = data?.winners?.length || 0;
      setStatus(`Raffle completed. Winners selected: ${count}.`);
    } catch (e) {
      console.error('Raffle run error:', e);
      setStatus(`Error: ${e.response?.data?.message || "Raffle run failed"}`);
    } finally {
      setSaving(false);
    }
  };

  const awardAuctionNow = async () => {
    setSaving(true);
    setStatus("Awarding auction…");
    try {
      const { data } = await axios.post(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/award`,
        {},
        { withCredentials: true }
      );
      const count = data?.winners?.length || 0;
      setStatus(`Auction awarded. Winners selected: ${count}.`);
    } catch (e) {
      console.error('Auction award error:', e);
      setStatus(`Error: ${e.response?.data?.message || "Auction award failed"}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container py-4" style={{ backgroundColor: "#3C3C3C", minHeight: "100vh" }}><p style={{ color: "#ffffff" }}>Loading…</p></div>;
  if (!item)   return <div className="container py-4" style={{ backgroundColor: "#3C3C3C", minHeight: "100vh" }}><p style={{ color: "#ffffff" }}>Item not found.</p></div>;

  return (
    <>
      <div className="container py-4" style={{ backgroundColor: "#3C3C3C", minHeight: "100vh" }}>
        <div className="grid md:grid-cols-2 gap-4" style={{ backgroundColor: "#3C3C3C" }}>
          {/* Media panel */}
          <div>
            {media.length > 0 ? (
              <div className="space-y-2">
                {media.map((m, idx) => (
                  <div key={idx} className="rounded overflow-hidden shadow">
                    {m.isVideo ? (
                      <video src={m.url} style={{ width:"100%", maxHeight: 400, objectFit:"contain" }} controls/>
                    ) : (
                      <img src={m.url} alt="item" style={{ width:"100%", maxHeight: 400, objectFit:"contain" }} />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded overflow-hidden shadow flex items-center justify-center" style={{ height: 400, backgroundColor: "#2a2a27" }}>
                <p style={{ color: "#9ca3af" }}>No media available</p>
              </div>
            )}
          </div>

          {/* Config panel */}
          <div className="card p-4 space-y-4 rounded shadow" style={{ backgroundColor: "#3C3C3C" }}>
            <div>
              <h2 className="font-semibold text-lg" style={{ color: "#ffffff" }}>
                {item?.name || `Item #${itemId}`}
              </h2>
              <p className="text-sm mt-1" style={{ color: "#d1d5db" }}>
                {actionType === "raffle" ? "Raffle Configuration" : "Auction Configuration"}
              </p>
            </div>

            {/* winners */}
            <div>
              <label className="block text-sm mb-1" style={{ color: "#e5e7eb" }}>
                # of winners{actionType === "raffle" ? " (per attribute combination)" : ""}
              </label>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="form-control"
                placeholder="How many winners?"
              />
            </div>

            {/* starting bid (auction only) - Moved here for better UX */}
            {actionType === "auction" && (
              <div>
                <label className="block text-sm mb-1 font-semibold" style={{ color: "#e5e7eb" }}>Starting Bid *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: "#6b7280" }}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control pl-8"
                    value={startingBid}
                    onChange={(e) => setStartingBid(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
                <small style={{ color: "#9ca3af" }}>Minimum bid amount for this auction</small>
              </div>
            )}

            {/* All Attributes Selection */}
            {Object.keys(allAttributes).length > 0 ? (
              <div className="space-y-3">
                <label className="block text-sm font-semibold" style={{ color: "#e5e7eb" }}>Select Attributes (Optional)</label>
                {Object.entries(allAttributes).map(([attrName, attrValues]) => (
                  <div key={attrName}>
                    <label className="block text-sm mb-1 capitalize" style={{ color: "#e5e7eb" }}>{attrName.replace(/_/g, ' ')}</label>
                    <select
                      className="form-control"
                      value={selectedAttributes[attrName] || ""}
                      onChange={(e) => setSelectedAttributes(prev => ({
                        ...prev,
                        [attrName]: e.target.value
                      }))}
                    >
                      <option value="">All {attrName.replace(/_/g, ' ')}</option>
                      {attrValues.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  </div>
                ))}
                {Object.keys(selectedAttributes).some(k => selectedAttributes[k]) && (
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary btn-sm" 
                    onClick={() => setSelectedAttributes({})}
                  >
                    Clear All Selections
                  </button>
                )}
              </div>
            ) : (
              <div className="text-sm" style={{ color: "#9ca3af" }}>
                No attributes found for this item
              </div>
            )}


            {/* timing */}
            <div className="card p-3 rounded shadow-sm" style={{ backgroundColor: "#4a4a45", border: "1px solid #5a5a55" }}>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  className={`btn btn-sm ${mode === "now" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setMode("now")}
                >
                  <FaPlay className="inline-block mr-1" />
                  Run now
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${mode === "schedule" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setMode("schedule")}
                >
                  <FaClock className="inline-block mr-1" />
                  Schedule
                </button>
              </div>

              {mode === "schedule" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: "#e5e7eb" }}>Start</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={startAt}
                      onChange={(e) => setStartAt(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: "#e5e7eb" }}>End</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={endAt}
                      onChange={(e) => setEndAt(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* public link */}
            <div className="card p-3 rounded shadow-sm" style={{ backgroundColor: "#4a4a45", border: "1px solid #5a5a55" }}>
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-2">
                  <div className="text-sm" style={{ color: "#9ca3af" }}>Public entry link</div>
                  <div className="truncate text-sm" style={{ color: "#d1d5db" }}>{publicLink}</div>
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={copyLink}>
                  <FaCopy className="inline-block mr-1" />
                  Copy
                </button>
              </div>
            </div>

            {status && (
              <p style={{ 
                marginTop: 12, 
                padding: 12, 
                borderRadius: 4, 
                backgroundColor: status.toLowerCase().includes("error") ? "#f8d7da" : "#d4edda", 
                color: status.toLowerCase().includes("error") ? "#721c24" : "#155724" 
              }}>
                {status}
              </p>
            )}
          </div>
        </div>
      </div>

      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
            disabled: saving
          },
          { icon: <FaHome />, label: "Home", onClick: goHome },
          { 
            icon: <FaBolt />, 
            label: actionType === 'raffle' ? 'Run Raffle Now' : 'Award Auction Now',
            onClick: actionType === 'raffle' ? runRaffleNow : awardAuctionNow, 
            disabled: saving 
          },
          { 
            icon: saving ? null : <FaSave />, 
            label: saving ? "Saving..." : "Save", 
            onClick: submit, 
            disabled: saving 
          },
        ]}
      />
    </>
  );
}